import {Avatar, Button, Input, Popconfirm, Select} from 'antd'
import {useFormikContext} from 'formik'
import _ from 'lodash'
import React, {useMemo, useState} from 'react'
import {IoChevronDown} from 'react-icons/io5'
import {PiFlagBanner, PiFlagBannerFill, PiTrash} from 'react-icons/pi'
import * as Yup from 'yup'
import useTranslate from '../../modules/local/useTranslate'
import {ListHeaderWithLine} from '../../views/AppLocals/Templates/items/ListHeader'
import {generateUUID, renderIf} from '../../views/Shared'
import {showDeleteConfirmDialog} from "../../views/AppLocals/factory/createConfirmDialog";
import {TbPlus, TbRowRemove} from "react-icons/tb";
import classNames from "classnames";
import {RiPictureInPictureFill, RiPictureInPictureLine} from "react-icons/ri";
import TooltipApp from "components/TooltipApp";

const {TextArea} = Input

// ─── Yup validation schema ────────────────────────────────────────────────────

export const dynamicFieldsSchema = Yup.array()
    .of(
        Yup.object().shape({
            title: Yup.string()
                .trim()
                .required('required')
                .max(120, 'title_max_120'),
            description: Yup.string()
                .trim()
                .max(500, 'description_max_500')
                .nullable(),
            ctrl_type: Yup.string().required(),
            options: Yup.array().when('ctrl_type', {
                is: function (v) { return ['radioField', 'checkboxField'].includes(v) },
                then: function (schema) {
                    return schema
                        .min(1, 'options_min_1')
                        .of(
                            Yup.string()
                                .trim()
                                .required('required')
                                .max(120, 'option_max_120')
                        )
                },
                otherwise: function (schema) { return schema.notRequired() },
            }),
        })
    )
    .nullable()

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const validateDynamicFields = async (items) => {
    try {
        await dynamicFieldsSchema.validate(items, {abortEarly: false})
        return {}
    } catch (err) {
        const errors = {}
        if (err.inner) {
            err.inner.forEach(function (e) {
                errors[e.path] = e.message
            })
        }
        return errors
    }
}

// ─── Controls defaults ────────────────────────────────────────────────────────

const controls = {
    toggleField: {
        title: '',
        description: '',
        ctrl_type: 'toggleField',
        options: [1, 0],
        answer_value: null,
    },
    radioField: {
        title: '',
        description: '',
        ctrl_type: 'radioField',
        options: ['option item ...'],
        answer_value: null,
    },
    checkboxField: {
        title: '',
        description: '',
        ctrl_type: 'checkboxField',
        options: ['option item ...'],
        answer_value: null,
    },
    inputField: {
        title: '',
        description: '',
        ctrl_type: 'inputField',
        options: [],
        answer_value: null,
    },
    textField: {
        title: '',
        description: '',
        ctrl_type: 'textField',
        options: [],
        answer_value: null,
    },
    dateField: {
        title: '',
        description: '',
        ctrl_type: 'dateField',
        options: [],
        answer_value: null,
    },
}

// ─── Inline error message ─────────────────────────────────────────────────────

const FieldError = ({message}) => {
    if (!message) return null
    return (
        <span className="text-red-500 text-xs italic leading-none">
            {message}
        </span>
    )
}

// ─── Single field row ─────────────────────────────────────────────────────────

const DynamicFieldDeclaration = ({items, index, handleChange, errors}) => {
    const t = useTranslate()
    const {values, setValues} = useFormikContext()

    // error key helpers — Yup paths for array items come as "[0].title" or "0.title"
    const fieldError = function (key) {
        return errors['[' + index + '].' + key] || errors[index + '.' + key]
    }
    const optionError = function (ii) {
        return (
            errors['[' + index + '].options[' + ii + ']'] ||
            errors[index + '.options[' + ii + ']'] ||
            errors['[' + index + '].options.' + ii]
        )
    }
    const optionsMinError = errors['[' + index + '].options'] || errors[index + '.options']

    const onUpdate = function (name, val) {
        setValues({...values, updated: Date.now()}).then(function () {
            var copyItems = _.cloneDeep(items)
            copyItems[index] = {...copyItems[index], [name]: val}
            handleChange(copyItems)
        })
    }
    const onDebouncedUpdate = _.debounce(onUpdate, 300)

    const onControlChange = function (controlType) {
        setValues({...values, updated: Date.now()}).then(function () {
            var copyItems = _.cloneDeep(items)
            var params = {...copyItems[index], ctrl_type: controlType}
            if (['toggleField'].includes(controlType)) {
                params.options = [1, 0]
            } else if (['radioField', 'checkboxField'].includes(controlType)) {
                params.options = copyItems[index] ? copyItems[index].options : []
            }
            copyItems[index] = params
            handleChange(copyItems)
        })
    }

    const onRemove = function () {
        setValues({...values, updated: Date.now()}).then(function () {
            var copyItems = _.cloneDeep(items)
            var newItems = copyItems.slice(0, index).concat(copyItems.slice(index + 1))
            handleChange(newItems)
        })
    }

    const onSetToggleFlag = function () {
        setValues({...values, updated: Date.now()}).then(function () {
            var copyItems = _.cloneDeep(items)
            var cur = copyItems[index] || {}
            copyItems[index] = {
                ...cur,
                isRequiredForRegistration: !cur.isRequiredForRegistration,
                is_mandatory: 0,
            }
            handleChange(copyItems)
        })
    }

    const onSetMandatoryToggleFlag = function () {
        setValues({...values, updated: Date.now()}).then(function () {
            var copyItems = _.cloneDeep(items)
            var cur = copyItems[index] || {}
            copyItems[index] = {
                ...cur,
                is_mandatory: !cur.is_mandatory,
            }
            handleChange(copyItems)
        })
    }

    const titleErr = fieldError('title')
    const descErr = fieldError('description')
    const isChoiceType = ['radioField', 'checkboxField'].includes(
        items[index] ? items[index].ctrl_type : ''
    )

    return (
        <div
            key={items[index] ? items[index].id : index}
            className="flex flex-col gap-2 mt-3">
            <ListHeaderWithLine
                title={
                    <span
                        style={{width: 24, height: 24}}
                        className="flex flex-center rounded-md background-200 font-bold text-sm text-color-100 leading-snug">
                {index + 1}
              </span>
                }
                extra={
                    <div className="flex flex-row space-x-3">
                        <TooltipApp
                            placement="topRight"
                            title={t('displayed on registration form')}
                            classNames={{root: 'font-medium text-xs italic'}}>
                            <Avatar
                                size={24}
                                onClick={onSetToggleFlag}
                                className={classNames(
                                    'flex flex-center cursor-pointer',
                                    !!(items[index] && items[index].isRequiredForRegistration)
                                        ? 'bg-primary-50 hover:bg-primary-100'
                                        : 'border border-color-50 background-100 hover:background-200'
                                )}
                                icon={
                                    !!(items[index] && items[index].isRequiredForRegistration)
                                        ? <PiFlagBannerFill size={14} className="text-primary"/>
                                        : <PiFlagBanner size={14} className="text-color-400"/>
                                }
                            />
                        </TooltipApp>

                        {!!(items[index] && items[index].isRequiredForRegistration) && (
                            <TooltipApp
                                placement="topRight"
                                title={t('mandatory for registrants to respond')}
                                classNames={{root: 'font-medium text-xs italic'}}>
                                <Avatar
                                    size={24}
                                    onClick={onSetMandatoryToggleFlag}
                                    className={classNames(
                                        'flex flex-center cursor-pointer',
                                        !!(items[index] && items[index].is_mandatory)
                                            ? 'bg-primary-50 hover:bg-primary-100'
                                            : 'border border-color-50 background-100 hover:background-200'
                                    )}
                                    icon={
                                        !!(items[index] && items[index].is_mandatory)
                                            ? <RiPictureInPictureFill size={14} className="text-primary"/>
                                            : <RiPictureInPictureLine size={14} className="text-color-400"/>
                                    }
                                />
                            </TooltipApp>
                        )}

                        <Popconfirm
                            icon={<PiTrash className="anticon text-primary-600" size={14}/>}
                            title={
                                <span className="font-medium text-sm text-color-100 italic">
                        {t('are you sure delete this question')}
                      </span>
                            }
                            placement="left"
                            onConfirm={onRemove}
                            okText={t('sure')}
                            cancelText={t('no')}
                            okButtonProps={{
                                type: 'primary',
                                className: 'button-rounded-md no-border font-medium text-xs px-3',
                            }}
                            cancelButtonProps={{
                                className: 'button-rounded-md no-border font-medium text-xs text-color-400 px-3',
                            }}>
                            <TooltipApp
                                title={t('delete question')}
                                placement="topRight"
                                classNames={{root: 'font-medium text-xs italic'}}>
                                <Avatar
                                    size={24}
                                    className="flex flex-center bg-primary-50 hover:bg-primary-100 cursor-pointer"
                                    icon={<PiTrash className="text-primary-600" size={14}/>}
                                />
                            </TooltipApp>
                        </Popconfirm>
                    </div>
                }
            />

            {/* Control type + Title */}
            <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-2">
                    <Select
                        defaultValue={items[index] ? items[index].ctrl_type : undefined}
                        style={{minWidth: 'fit-content'}}
                        onChange={onControlChange}
                        suffixIcon={<IoChevronDown className="text-color-300"/>}
                        options={[
                            {value: 'toggleField', label: t('toggle field')},
                            {value: 'radioField', label: t('radio field')},
                            {value: 'checkboxField', label: t('checkbox field')},
                            {value: 'inputField', label: t('input field')},
                            {value: 'textField', label: t('text field')},
                            {value: 'dateField', label: t('date field')},
                        ]}
                    />
                    <div className="flex flex-col gap-1 flex-1">
                        <Input
                            className={classNames(
                                'w-full rounded-md',
                                titleErr && 'border-red-400'
                            )}
                            status={titleErr ? 'error' : undefined}
                            placeholder={t('title') + ' *'}
                            showCount
                            maxLength={120}
                            onChange={function (e) { onDebouncedUpdate('title', e.target.value) }}
                            defaultValue={items[index] ? items[index].title : ''}
                        />
                        {titleErr && <FieldError message={t(titleErr)}/>}
                    </div>
                </div>
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1">
                <TextArea
                    style={{width: '100%'}}
                    status={descErr ? 'error' : undefined}
                    rows={3}
                    placeholder={t('description')}
                    showCount
                    maxLength={500}
                    onChange={function (e) { onDebouncedUpdate('description', e.target.value) }}
                    defaultValue={items[index] ? items[index].description : ''}
                />
                {descErr && <FieldError message={t(descErr)}/>}
            </div>

            {/* Options (radio / checkbox) */}
            {renderIf(
                isChoiceType,
                <div className="flex flex-col gap-2 ml-4">
                    <ListHeaderWithLine
                        className="text-xs italic"
                        title={
                            <span>
                      {t('options')}
                                {optionsMinError && (
                                    <FieldError message={' — ' + t(optionsMinError)}/>
                                )}
                    </span>
                        }
                        extra={
                            <Button
                                type="text"
                                size="small"
                                className="button-rounded-md bg-primary-50 hover:bg-primary-100 font-medium text-xs text-primary italic"
                                onClick={function () {
                                    var opts = items[index] ? items[index].options : []
                                    onDebouncedUpdate('options', opts.concat(['']))
                                }}>
                                {t('add options')}
                            </Button>
                        }
                    />
                    <div className="flex flex-col gap-2">
                        {(items[index] ? items[index].options : []).map(function (ee, ii) {
                            const optErr = optionError(ii)
                            return (
                                <div key={ii} className="flex flex-col gap-1">
                                    <Input
                                        style={{padding: '4px 6px'}}
                                        status={optErr ? 'error' : undefined}
                                        className={classNames(
                                            'w-full rounded-md',
                                            optErr && 'border-red-400'
                                        )}
                                        placeholder={t('content answer') + ' *'}
                                        showCount
                                        maxLength={120}
                                        prefix={<TbPlus size={16} className="text-color-400"/>}
                                        suffix={
                                            <TooltipApp
                                                title={t('delete option')}
                                                placement="left"
                                                style={{
                                                    body: {minHeight: 30, fontSize: 12, fontStyle: 'italic'},
                                                }}>
                                                <TbRowRemove
                                                    className="text-color-300 hover:text-primary cursor-pointer"
                                                    onClick={function () {
                                                        showDeleteConfirmDialog({
                                                            translate: t,
                                                            title: 'delete option',
                                                            message: 'are you sure you want to delete option',
                                                            onOk: function () {
                                                                var copyOptions = _.cloneDeep(items[index] ? items[index].options : [])
                                                                var newOptions = copyOptions
                                                                    .slice(0, ii)
                                                                    .concat(copyOptions.slice(ii + 1))
                                                                onDebouncedUpdate('options', newOptions)
                                                            },
                                                        })
                                                    }}
                                                />
                                            </TooltipApp>
                                        }
                                        onChange={function (e) {
                                            var options = (items[index] ? items[index].options : []).slice()
                                            options[ii] = e.target.value
                                            onDebouncedUpdate('options', options)
                                        }}
                                        defaultValue={ee}
                                    />
                                    {optErr && <FieldError message={t(optErr)}/>}
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

// ─── Container ────────────────────────────────────────────────────────────────

const DynamicFieldsDeclaration = ({
                                      name,
                                      value,
                                      onChange,
                                      className,
                                  }) => {
    const t = useTranslate()
    const safeValue = value || []
    const items = useMemo(function () {
        return Array.from(safeValue)
    }, [safeValue])

    // Self-contained error state — no need for parent form to handle validation
    const [fieldErrors, setFieldErrors] = useState({})

    const handleChange = function (nextItems) {
        validateDynamicFields(nextItems).then(function (errs) {
            setFieldErrors(errs)
        })
        onChange(nextItems)
    }

    return (
        <div className="flex flex-col space-y-3">
            <div className="flex flex-row gap-3 justify-end">
                <Button
                    color="primary" variant="filled"
                    className="rounded-md no-border font-medium text-xs leading-tight px-3"
                    onClick={function () {
                        var id = generateUUID()
                        var next = safeValue.concat([{...controls.inputField, id: id}])
                        // Validate immediately so new row shows title error right away
                        validateDynamicFields(next).then(function (errs) {
                            setFieldErrors(errs)
                        })
                        onChange(next)
                    }}>
                    {t('add new')}
                </Button>
            </div>
            <div className="flex flex-col space-y-6">
                {items.map(function (e, i, array) {
                    return (
                        <DynamicFieldDeclaration
                            key={(e && e.id) ? e.id : i}
                            index={i}
                            value={e}
                            items={array}
                            errors={fieldErrors}
                            handleChange={handleChange}
                        />
                    )
                })}
            </div>
        </div>
    )
}

export default DynamicFieldsDeclaration